import React, { useState, useMemo } from 'react';
import { X, Clock, BookOpen, Check, User, Search, Repeat, AlertTriangle, Info, DoorOpen, GripVertical, Calendar, Moon, Loader2 } from 'lucide-react';
import type { SessionType } from '../../services/calendarSettingsService';
import type { ModuleElement } from '@/types/filiere.types';
import type { StaffUser } from '@/services/userService';
import type { RoomFlat } from '@/services/campusInfrastructureService';
import type { BlockedDate, RamadanPeriod } from '../../services/scheduleService';

type RecurrenceMode = 'single' | 'until_volume';

interface SessionTypeModalProps {
  matieres: ModuleElement[];
  selectedMatiere: ModuleElement;
  sessionTypes: SessionType[];
  professors: StaffUser[];
  rooms?: RoomFlat[];
  semesterStartDate?: string;
  semesterEndDate?: string;
  selectedDayOfWeek?: number;
  selectedStartTime?: string;
  selectedDate?: string; // Concrete date of the clicked cell (YYYY-MM-DD)
  outOfSemester?: boolean; // User confirmed out-of-semester scheduling — widen projection window
  scheduledHoursMap?: Map<string, number>; // Map of matiereId -> already scheduled hours
  blockedDates?: BlockedDate[]; // Holidays and vacations
  ramadanPeriod?: RamadanPeriod | null; // Ramadan period for duration adjustments
  isSubmitting?: boolean; // A creation is in flight: ignore further clicks
  onConfirm: (
    sessionTypeId: string,
    matiereId?: string,
    professorId?: string,
    recurrenceMode?: RecurrenceMode,
    recurrenceWeeks?: number,
    roomId?: string,
    ramadanStartTime?: string,
    ramadanEndTime?: string,
    paidDurationMinutes?: number
  ) => void;
  onClose: () => void;
}

const DAY_LABELS: Record<number, string> = {
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
  0: 'Dimanche',
};

export const SessionTypeModal: React.FC<SessionTypeModalProps> = ({
  matieres,
  selectedMatiere,
  sessionTypes,
  professors,
  rooms = [],
  semesterStartDate,
  semesterEndDate,
  selectedDayOfWeek,
  selectedStartTime,
  selectedDate,
  outOfSemester = false,
  scheduledHoursMap,
  blockedDates = [],
  ramadanPeriod,
  isSubmitting = false,
  onConfirm,
  onClose,
}) => {
  const [selectedMatiereId, setSelectedMatiereId] = useState<string>(selectedMatiere.id);
  const [selectedSessionTypeId, setSelectedSessionTypeId] = useState<string>(
    sessionTypes.length > 0 ? sessionTypes[0].id : ''
  );
  const [selectedProfessorId, setSelectedProfessorId] = useState<string>('');
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [showMatiereSelector, setShowMatiereSelector] = useState(false);
  const [showProfessorSelector, setShowProfessorSelector] = useState(false);
  const [showRoomSelector, setShowRoomSelector] = useState(false);
  const [professorSearchQuery, setProfessorSearchQuery] = useState('');
  const [roomSearchQuery, setRoomSearchQuery] = useState('');

  // Ramadan time state - only used when sessions span both Ramadan and non-Ramadan periods
  const [ramadanStartTime, setRamadanStartTime] = useState<string>('');

  // Recurrence state - default to 'until_volume' if volume horaire is defined, otherwise 'single'
  const [recurrenceMode, setRecurrenceMode] = useState<RecurrenceMode>(
    selectedMatiere.volumeHoraire && selectedMatiere.volumeHoraire > 0 ? 'until_volume' : 'single'
  );

  const handleConfirm = () => {
    if (isSubmitting) return;
    if (selectedSessionTypeId && selectedProfessorId) {
      // Calculate Ramadan end time if Ramadan start time is specified
      let ramadanEndTimeValue: string | undefined;
      if (ramadanStartTime && needsRamadanTimeInput) {
        const [rStartHour, rStartMin] = ramadanStartTime.split(':').map(Number);
        const rEndMinutes = rStartHour * 60 + rStartMin + ramadanDurationMinutes;
        const rEndHour = Math.floor(rEndMinutes / 60);
        const rEndMin = rEndMinutes % 60;
        ramadanEndTimeValue = `${rEndHour.toString().padStart(2, '0')}:${rEndMin.toString().padStart(2, '0')}`;
      }

      onConfirm(
        selectedSessionTypeId,
        selectedMatiereId,
        selectedProfessorId,
        recurrenceMode,
        undefined,
        selectedRoomId || undefined,
        needsRamadanTimeInput && ramadanStartTime ? ramadanStartTime : undefined,
        ramadanEndTimeValue,
        undefined // paid duration comes from the session type; per-course changes go through the edit modal
      );
    }
  };

  const currentRoom = rooms.find(r => r.id === selectedRoomId);

  // Filter rooms by search query
  const filteredRooms = rooms.filter(room => {
    if (!roomSearchQuery) return true;
    const query = roomSearchQuery.toLowerCase();
    return room.name.toLowerCase().includes(query) ||
      room.fullName.toLowerCase().includes(query) ||
      room.floorName.toLowerCase().includes(query);
  });

  const currentMatiere = matieres.find(m => m.id === selectedMatiereId) || selectedMatiere;
  const currentSessionType = sessionTypes.find(st => st.id === selectedSessionTypeId);
  const currentProfessor = professors.find(p => p.id === selectedProfessorId);

  // Get already scheduled hours for this matière
  const alreadyScheduledHours = scheduledHoursMap?.get(selectedMatiereId) || 0;

  // Volume horaire and basic durations
  const volumeHoraire = currentMatiere.volumeHoraire || 0;
  const remainingHours = Math.max(0, volumeHoraire - alreadyScheduledHours);
  const regularDurationMinutes = currentSessionType?.durationMinutes || 120;

  // Durée rémunérée of the session type drives the volume horaire (Ramadan-aware)
  const paidReferenceMinutes = currentSessionType?.paidDurationMinutes ?? regularDurationMinutes;
  const ramadanPaidReferenceMinutes = currentSessionType?.ramadanPaidDurationMinutes ?? null;
  const toPaidMinutes = (actual: number, inRamadan = false): number => {
    if (inRamadan && currentSessionType?.ramadanBehavior === 'SHORTEN' && currentSessionType.ramadanDurationMinutes) {
      const ramadanPaid = ramadanPaidReferenceMinutes
        ?? (paidReferenceMinutes * currentSessionType.ramadanDurationMinutes) / regularDurationMinutes;
      return (actual * ramadanPaid) / currentSessionType.ramadanDurationMinutes;
    }
    return regularDurationMinutes > 0 ? (actual * paidReferenceMinutes) / regularDurationMinutes : actual;
  };
  // Hours counted per session for the volume horaire = PAID hours
  const regularDurationHours = paidReferenceMinutes / 60;

  // Ramadan duration (if session type is configured to shorten during Ramadan)
  const ramadanDurationMinutes = (
    currentSessionType?.ramadanBehavior === 'SHORTEN' &&
    currentSessionType?.ramadanDurationMinutes
  ) ? currentSessionType.ramadanDurationMinutes : regularDurationMinutes;

  // Calculate accurate session projection that mirrors backend logic
  const sessionProjection = useMemo(() => {
    // Helper to check if a date is in Ramadan (defined inside useMemo to capture current ramadanPeriod)
    const checkDateInRamadan = (date: Date): boolean => {
      if (!ramadanPeriod) return false;
      const dateStr = date.toISOString().split('T')[0];
      return dateStr >= ramadanPeriod.startDate && dateStr <= ramadanPeriod.endDate;
    };

    // Helper to check if a date is blocked (holiday or vacation)
    const checkDateBlocked = (date: Date): boolean => {
      const dateStr = date.toISOString().split('T')[0];
      return blockedDates.some(b => b.date === dateStr);
    };

    // Debug logging
    console.log('[SessionProjection] Inputs:', {
      semesterStartDate,
      semesterEndDate,
      selectedDayOfWeek,
      remainingHours,
      regularDurationMinutes,
      ramadanDurationMinutes,
      blockedDatesCount: blockedDates.length,
      ramadanPeriod,
      sessionTypeRamadanBehavior: currentSessionType?.ramadanBehavior,
      sessionTypeRamadanDuration: currentSessionType?.ramadanDurationMinutes,
    });

    if (!semesterStartDate || !semesterEndDate || selectedDayOfWeek === undefined) {
      // Fallback to simple calculation
      const sessionsNeeded = remainingHours > 0 ? Math.ceil(remainingHours / regularDurationHours) : 0;
      console.log('[SessionProjection] Fallback: missing data, using simple calculation');
      return {
        sessionsNeeded,
        actualSessions: sessionsNeeded,
        totalHours: sessionsNeeded * regularDurationHours,
        ramadanSessions: 0,
        regularSessions: sessionsNeeded,
        hasEnoughTime: true,
      };
    }

    const semesterStart = new Date(semesterStartDate);
    let semesterEnd = new Date(semesterEndDate);
    const today = new Date();
    today.setHours(12, 0, 0, 0); // Normalize to noon

    // Start from today or semester start, whichever is later
    let startFromDate = today > semesterStart ? today : semesterStart;

    // Whole semester already past: sessions are generated retroactively from the
    // semester start (mirrors backend generateSessions behavior)
    if (today > semesterEnd) {
      startFromDate = semesterStart;
    }

    // The clicked cell's date raises the start further: sessions begin from the
    // selected week, never earlier (mirrors backend explicit startDate handling)
    if (selectedDate) {
      const clicked = new Date(selectedDate + 'T12:00:00');
      if (!isNaN(clicked.getTime())) {
        if (outOfSemester) {
          // Confirmed out-of-semester: clicked date defines the window (mirrors backend)
          startFromDate = clicked;
          semesterEnd = new Date(clicked);
          semesterEnd.setDate(semesterEnd.getDate() + 365);
        } else if (clicked > startFromDate) {
          startFromDate = clicked;
        }
      }
    }

    // Find the first occurrence of the target day of week
    const currentDate = new Date(startFromDate);
    currentDate.setHours(12, 0, 0, 0);
    const currentDayOfWeek = currentDate.getDay();
    const targetDayOfWeek = selectedDayOfWeek;
    const daysUntilTarget = (targetDayOfWeek - currentDayOfWeek + 7) % 7;
    currentDate.setDate(currentDate.getDate() + daysUntilTarget);

    console.log('[SessionProjection] Starting simulation from:', currentDate.toISOString().split('T')[0], 'until', semesterEnd.toISOString().split('T')[0]);

    // Simulate session generation
    const targetMinutes = remainingHours * 60;
    let accumulatedMinutes = 0;
    let sessionCount = 0;
    let ramadanSessionCount = 0;
    let regularSessionCount = 0;
    const maxIterations = 52; // Safety limit
    const sessionDates: string[] = []; // For debugging

    while (accumulatedMinutes < targetMinutes && sessionCount < maxIterations) {
      // Check if past semester end
      if (currentDate > semesterEnd) break;

      const dateStr = currentDate.toISOString().split('T')[0];
      const isBlocked = checkDateBlocked(currentDate);
      const inRamadan = checkDateInRamadan(currentDate);

      // Skip blocked dates (holidays/vacations)
      if (!isBlocked) {
        const sessionMinutes = inRamadan ? ramadanDurationMinutes : regularDurationMinutes;

        // Volume horaire is consumed in PAID minutes
        accumulatedMinutes += toPaidMinutes(sessionMinutes, inRamadan);
        sessionCount++;
        sessionDates.push(`${dateStr}${inRamadan ? ' [R]' : ''}`);

        if (inRamadan) {
          ramadanSessionCount++;
        } else {
          regularSessionCount++;
        }
      }

      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7);
    }

    // Simple calculation for "sessions needed" (without accounting for Ramadan/blocked dates)
    const simpleSessionsNeeded = remainingHours > 0 ? Math.ceil(remainingHours / regularDurationHours) : 0;

    console.log('[SessionProjection] Result:', {
      simpleSessionsNeeded,
      actualSessionCount: sessionCount,
      totalHours: accumulatedMinutes / 60,
      ramadanSessionCount,
      regularSessionCount,
      targetMinutes,
      accumulatedMinutes,
      hasEnoughTime: accumulatedMinutes >= targetMinutes,
      sessionDates: sessionDates.slice(0, 10), // First 10 for logging
    });

    // Determine if we need to ask for Ramadan time input
    // This is true when sessions span BOTH Ramadan and non-Ramadan periods
    // AND the session type is configured to shorten during Ramadan
    const spansBothPeriods = ramadanSessionCount > 0 && regularSessionCount > 0;

    return {
      sessionsNeeded: simpleSessionsNeeded,
      actualSessions: sessionCount,
      totalHours: accumulatedMinutes / 60,
      ramadanSessions: ramadanSessionCount,
      regularSessions: regularSessionCount,
      hasEnoughTime: accumulatedMinutes >= targetMinutes,
      spansBothPeriods,
    };
  }, [
    semesterStartDate,
    semesterEndDate,
    selectedDayOfWeek,
    selectedDate,
    outOfSemester,
    remainingHours,
    regularDurationMinutes,
    paidReferenceMinutes,
    ramadanPaidReferenceMinutes,
    ramadanDurationMinutes,
    blockedDates,
    ramadanPeriod,
    regularDurationHours,
    currentSessionType?.ramadanBehavior,
    currentSessionType?.ramadanDurationMinutes,
  ]);

  // Check if volume horaire is already complete
  const isVolumeComplete = volumeHoraire > 0 && alreadyScheduledHours >= volumeHoraire;

  // Use projected values
  const sessionsNeeded = sessionProjection.sessionsNeeded;
  const actualSessionsToCreate = sessionProjection.actualSessions;
  const hoursToSchedule = sessionProjection.totalHours;
  const hasEnoughTime = sessionProjection.hasEnoughTime;
  const hasRamadanImpact = sessionProjection.ramadanSessions > 0 && ramadanDurationMinutes < regularDurationMinutes;

  // Determine if we need to ask for Ramadan time input
  // Only when: recurring mode, sessions span both periods, session type shortens during Ramadan
  const needsRamadanTimeInput =
    recurrenceMode !== 'single' &&
    sessionProjection.spansBothPeriods &&
    currentSessionType?.ramadanBehavior === 'SHORTEN' &&
    ramadanDurationMinutes < regularDurationMinutes;

  // Filter professors by search query
  const filteredProfessors = professors.filter(prof => {
    if (!professorSearchQuery) return true;
    const query = professorSearchQuery.toLowerCase();
    const fullName = `${prof.firstName} ${prof.lastName}`.toLowerCase();
    const reverseName = `${prof.lastName} ${prof.firstName}`.toLowerCase();
    return fullName.includes(query) || reverseName.includes(query) || prof.staffCode?.toLowerCase().includes(query);
  });

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}`;
  };

  // Calculate end time based on session type
  const getEndTime = (startTime: string, durationMinutes: number): string => {
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Nouvelle séance</h3>
                {selectedDayOfWeek !== undefined && selectedStartTime && (
                  <p className="text-sm text-gray-500">
                    {selectedDate
                      ? `${new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} à ${selectedStartTime}`
                      : `${DAY_LABELS[selectedDayOfWeek]} à ${selectedStartTime}`}
                    {outOfSemester && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Hors semestre — confirmé
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Selected Time Slot Badge */}
          {selectedDayOfWeek !== undefined && selectedStartTime && currentSessionType && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm">
              <Clock size={14} className="text-gray-500" />
              <span className="text-gray-700 font-medium">
                {selectedStartTime} - {getEndTime(selectedStartTime, currentSessionType.durationMinutes)}
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-600">{formatDuration(currentSessionType.durationMinutes)}</span>
            </div>
          )}
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Selected Matière - Compact Card Style */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Matière
            </label>
            {showMatiereSelector ? (
              <div className="space-y-2">
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl">
                  {matieres.map((matiere) => (
                    <button
                      key={matiere.id}
                      onClick={() => {
                        setSelectedMatiereId(matiere.id);
                        setShowMatiereSelector(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        selectedMatiereId === matiere.id ? 'bg-primary-50' : ''
                      }`}
                    >
                      <BookOpen size={16} className="text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {matiere.name}
                        </p>
                        {matiere.volumeHoraire && (
                          <p className="text-xs text-gray-500">{matiere.volumeHoraire}h</p>
                        )}
                      </div>
                      {selectedMatiereId === matiere.id && (
                        <Check size={16} className="text-primary-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowMatiereSelector(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Annuler
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowMatiereSelector(true)}
                className="w-full px-4 py-3 text-left bg-gray-50 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-white transition-all flex items-center gap-3 group"
              >
                <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center group-hover:border-primary-300 transition-colors">
                  <BookOpen size={16} className="text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {currentMatiere.name}
                  </p>
                  {volumeHoraire > 0 && (
                    <p className="text-xs text-gray-500">{volumeHoraire}h de volume horaire</p>
                  )}
                </div>
                <GripVertical size={14} className="text-gray-300" />
              </button>
            )}
          </div>

          {/* Session Type Selection - Card Grid */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Type de séance
            </label>
            {sessionTypes.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {sessionTypes.map((sessionType) => (
                  <button
                    key={sessionType.id}
                    onClick={() => setSelectedSessionTypeId(sessionType.id)}
                    className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                      selectedSessionTypeId === sessionType.id
                        ? 'border-primary-500 bg-primary-50 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: sessionType.color || '#6366F1' }}
                      />
                      {selectedSessionTypeId === sessionType.id && (
                        <Check size={14} className="text-primary-600" />
                      )}
                    </div>
                    <p className="font-medium text-gray-900 text-sm leading-tight">
                      {sessionType.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDuration(sessionType.durationMinutes)}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-300 rounded-xl bg-gray-50">
                Aucun type de séance configuré.
              </div>
            )}
          </div>

          {/* Professor Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Professeur <span className="text-red-500">*</span>
            </label>
            {showProfessorSelector ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un professeur..."
                    value={professorSearchQuery}
                    onChange={(e) => setProfessorSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl">
                  {filteredProfessors.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      Aucun professeur trouvé
                    </div>
                  ) : (
                    filteredProfessors.map((professor) => (
                      <button
                        key={professor.id}
                        onClick={() => {
                          setSelectedProfessorId(professor.id);
                          setShowProfessorSelector(false);
                          setProfessorSearchQuery('');
                        }}
                        className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                          selectedProfessorId === professor.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                          {professor.firstName?.[0]}{professor.lastName?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {professor.lastName} {professor.firstName}
                          </p>
                          {professor.staffCode && (
                            <p className="text-xs text-gray-500">{professor.staffCode}</p>
                          )}
                        </div>
                        {selectedProfessorId === professor.id && (
                          <Check size={16} className="text-primary-600 flex-shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowProfessorSelector(false);
                    setProfessorSearchQuery('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Annuler
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowProfessorSelector(true)}
                className={`w-full px-4 py-3 text-left rounded-xl border transition-all flex items-center gap-3 group ${
                  currentProfessor
                    ? 'bg-gray-50 border-gray-200 hover:border-primary-300 hover:bg-white'
                    : 'border-dashed border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
              >
                {currentProfessor ? (
                  <>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-xs font-medium text-primary-700">
                      {currentProfessor.firstName?.[0]}{currentProfessor.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {currentProfessor.lastName} {currentProfessor.firstName}
                      </p>
                      {currentProfessor.staffCode && (
                        <p className="text-xs text-gray-500">{currentProfessor.staffCode}</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <User size={16} className="text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500">Sélectionner un professeur</p>
                    </div>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Room Selection (Optional) */}
          {rooms.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Salle <span className="text-gray-400 font-normal normal-case">(optionnel)</span>
              </label>
              {showRoomSelector ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une salle..."
                      value={roomSearchQuery}
                      onChange={(e) => setRoomSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl">
                    <button
                      onClick={() => {
                        setSelectedRoomId('');
                        setShowRoomSelector(false);
                        setRoomSearchQuery('');
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        !selectedRoomId ? 'bg-gray-50' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        <DoorOpen size={14} className="text-gray-400" />
                      </div>
                      <span className="text-sm text-gray-500 italic">Sans salle attribuée</span>
                    </button>
                    {filteredRooms.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        Aucune salle trouvée
                      </div>
                    ) : (
                      filteredRooms.map((room) => (
                        <button
                          key={room.id}
                          onClick={() => {
                            setSelectedRoomId(room.id);
                            setShowRoomSelector(false);
                            setRoomSearchQuery('');
                          }}
                          className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                            selectedRoomId === room.id ? 'bg-primary-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <DoorOpen size={14} className="text-blue-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {room.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {room.floorName}
                              {room.capacity && ` • ${room.capacity} places`}
                            </p>
                          </div>
                          {selectedRoomId === room.id && (
                            <Check size={16} className="text-primary-600 flex-shrink-0" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowRoomSelector(false);
                      setRoomSearchQuery('');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowRoomSelector(true)}
                  className={`w-full px-4 py-3 text-left rounded-xl border transition-all flex items-center gap-3 ${
                    currentRoom
                      ? 'bg-gray-50 border-gray-200 hover:border-primary-300 hover:bg-white'
                      : 'border-dashed border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    currentRoom ? 'bg-blue-50' : 'bg-gray-100'
                  }`}>
                    <DoorOpen size={14} className={currentRoom ? 'text-blue-500' : 'text-gray-400'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {currentRoom ? (
                      <>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {currentRoom.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentRoom.floorName}
                          {currentRoom.capacity && ` • ${currentRoom.capacity} places`}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">Ajouter une salle</p>
                    )}
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Recurrence Options - Simplified */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              <div className="flex items-center gap-2">
                <Repeat size={12} />
                Récurrence
              </div>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setRecurrenceMode('single')}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  recurrenceMode === 'single'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <p className="text-sm font-medium text-gray-900">Unique</p>
                <p className="text-xs text-gray-500 mt-0.5">Une seule séance</p>
              </button>

              <button
                onClick={() => volumeHoraire > 0 && setRecurrenceMode('until_volume')}
                disabled={volumeHoraire === 0}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  recurrenceMode === 'until_volume'
                    ? 'border-primary-500 bg-primary-50'
                    : volumeHoraire === 0
                    ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${volumeHoraire === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                      Volume horaire
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {volumeHoraire > 0 ? `${sessionsNeeded} séances` : 'Non défini'}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Ramadan Time Input - Only shown when sessions span both periods */}
          {needsRamadanTimeInput && (
            <div className="p-4 rounded-xl border bg-purple-50 border-purple-200">
              <div className="flex items-start gap-3">
                <Moon className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-900 mb-2">
                    Horaire Ramadan
                  </p>
                  <p className="text-xs text-purple-700 mb-3">
                    Certaines séances tombent pendant le Ramadan ({sessionProjection.ramadanSessions} sur {actualSessionsToCreate}).
                    Veuillez spécifier l'heure de début pour ces séances.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-purple-800 mb-1">
                        Heure de début (Ramadan)
                      </label>
                      <input
                        type="time"
                        value={ramadanStartTime}
                        onChange={(e) => setRamadanStartTime(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        placeholder="09:00"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-purple-800 mb-1">
                        Durée Ramadan
                      </label>
                      <div className="px-3 py-2 text-sm bg-purple-100 border border-purple-200 rounded-lg text-purple-800">
                        {formatDuration(ramadanDurationMinutes)}
                      </div>
                    </div>
                    {ramadanStartTime && (
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-purple-800 mb-1">
                          Heure de fin
                        </label>
                        <div className="px-3 py-2 text-sm bg-purple-100 border border-purple-200 rounded-lg text-purple-800">
                          {getEndTime(ramadanStartTime, ramadanDurationMinutes)}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-purple-600 mt-2 italic">
                    Les séances hors Ramadan utiliseront l'horaire normal: {selectedStartTime} - {selectedStartTime ? getEndTime(selectedStartTime, regularDurationMinutes) : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Already Scheduled Progress */}
          {volumeHoraire > 0 && alreadyScheduledHours > 0 && (
            <div className="p-3 rounded-xl border bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-blue-800">Déjà planifié</span>
                <span className="text-xs font-semibold text-blue-900">
                  {alreadyScheduledHours}h / {volumeHoraire}h
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (alreadyScheduledHours / volumeHoraire) * 100)}%` }}
                />
              </div>
              {isVolumeComplete && (
                <p className="text-xs text-blue-700 mt-2 flex items-center gap-1">
                  <Check size={12} />
                  Volume horaire complet
                </p>
              )}
            </div>
          )}

          {/* Summary Card */}
          {recurrenceMode === 'until_volume' && volumeHoraire > 0 && !isVolumeComplete && (
            <div className={`p-4 rounded-xl border ${hasEnoughTime ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-3">
                {hasEnoughTime ? (
                  <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${hasEnoughTime ? 'text-green-800' : 'text-amber-800'}`}>
                    {actualSessionsToCreate} séance{actualSessionsToCreate > 1 ? 's' : ''} seront créées
                    {!hasEnoughTime && ` (${sessionsNeeded} requises)`}
                  </p>
                  <p className={`text-xs mt-1 ${hasEnoughTime ? 'text-green-700' : 'text-amber-700'}`}>
                    +{Math.round(hoursToSchedule * 10) / 10}h → {Math.round((alreadyScheduledHours + hoursToSchedule) * 10) / 10}h sur {volumeHoraire}h
                    {!hasEnoughTime && ` • ${Math.round((remainingHours - hoursToSchedule) * 10) / 10}h manquantes`}
                  </p>
                  {/* Ramadan impact indicator */}
                  {hasRamadanImpact && (
                    <p className="text-xs mt-2 flex items-center gap-1.5 text-purple-700">
                      <Moon size={12} className="text-purple-500" />
                      {sessionProjection.ramadanSessions} séance{sessionProjection.ramadanSessions > 1 ? 's' : ''} pendant Ramadan ({formatDuration(ramadanDurationMinutes)} au lieu de {formatDuration(regularDurationMinutes)})
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Volume Complete Warning */}
          {recurrenceMode === 'until_volume' && isVolumeComplete && (
            <div className="p-4 rounded-xl border bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">
                    Volume horaire déjà complet
                  </p>
                  <p className="text-xs mt-1 text-blue-700">
                    Cette matière a déjà {alreadyScheduledHours}h planifiées sur {volumeHoraire}h requises.
                    Vous pouvez ajouter une séance unique si nécessaire.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || !selectedSessionTypeId || !selectedProfessorId}
            className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Création en cours...' : 'Créer la séance'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTypeModal;
